import React, { useEffect } from "react";
import { Platform, ScrollView, StyleSheet, View, type ViewStyle } from "react-native";
import type { GetModernizedCoursePublishHistoryQuery } from "@axio/graphql/generated/graphql";

import PublishHistorySheet from "./components/PublishHistorySheet";
import PublishSheet from "./components/PublishSheet";
import ReviewActionPanel from "./components/ReviewActionPanel";
import ReviewContentPanel from "./components/ReviewContentPanel";
import ReviewSidebar from "./components/ReviewSidebar";
import ReviewTopBar from "./components/ReviewTopBar";
import type {
  CourseEvaluatorReviewDecision,
  CourseEvaluatorReviewData,
  CourseEvaluatorReviewSidebarGroup,
  CourseEvaluatorReviewSidebarItem,
} from "./types/reviewTypes";
import type {
  ActivityDetail,
  PublishedPublishItem,
  PublishDocumentUpdate,
  PublishManualStep,
  PublishStatus,
  QueuedPublishItem,
  StructuredFields,
} from "./types/reviewTypes";
import { colors } from "../../../../styles/design-system";

type PublishHistoryRecord =
  GetModernizedCoursePublishHistoryQuery["getModernizedCoursePublishHistory"]["records"][number];

interface ReviewPanelProps {
  review: CourseEvaluatorReviewData;
  activityDetails: Record<string, ActivityDetail>;
  selectedItemId: string | null;
  selectedReviewItem: CourseEvaluatorReviewSidebarItem | null;
  selectedDecision: CourseEvaluatorReviewDecision;
  filterMode: "all" | "undecided";
  collapsedSectionIds: Set<string>;
  reviewedProgress: number;
  getDecision: (id: string) => CourseEvaluatorReviewDecision;
  lastSavedAt?: string | null;
  isSaving?: boolean;
  onExitReview: () => void;
  onSelectItem: (itemId: string | null) => void;
  onToggleSection: (sectionId: string) => void;
  onFilterModeChange: (mode: "all" | "undecided") => void;
  onStartReviewing: () => void;
  onAccept?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onReject?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onAcceptGroup?: (group: CourseEvaluatorReviewSidebarGroup) => void;
  onRejectGroup?: (group: CourseEvaluatorReviewSidebarGroup) => void;
  onUndo?: () => void;
  onSaveEdit?: (item: CourseEvaluatorReviewSidebarItem, editedFields: StructuredFields) => void;
  onCreateIle?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onNavigateNext?: () => void;
  onNavigatePrev?: () => void;
  // Publish flow
  queuedPublishItems: QueuedPublishItem[];
  lastPublishedItems: PublishedPublishItem[];
  publishManualSteps: PublishManualStep[];
  publishDocumentUpdates: PublishDocumentUpdate[];
  undecidedCount: number;
  isPublishSheetOpen: boolean;
  publishStatus: PublishStatus;
  onOpenPublishSheet: () => void;
  onClosePublishSheet: () => void;
  onDonePublish: () => void;
  onPublish: () => void;
  publishHistoryRecords: PublishHistoryRecord[];
  pendingManualStepsCount: number;
  isPublishHistoryOpen: boolean;
  isClearingPublishHistory: boolean;
  onOpenPublishHistory: () => void;
  onClosePublishHistory: () => void;
  onClearPublishHistory: () => void;
  canAcceptSelectedItem?: boolean;
  acceptDisabledReason?: string;
}

function ReviewPanel({
  review,
  activityDetails,
  selectedItemId,
  selectedReviewItem,
  selectedDecision,
  filterMode,
  collapsedSectionIds,
  reviewedProgress,
  getDecision,
  lastSavedAt,
  isSaving,
  onExitReview,
  onSelectItem,
  onToggleSection,
  onFilterModeChange,
  onStartReviewing,
  onAccept,
  onReject,
  onAcceptGroup,
  onRejectGroup,
  onUndo,
  onSaveEdit,
  onCreateIle,
  onNavigateNext,
  onNavigatePrev,
  queuedPublishItems,
  lastPublishedItems,
  publishManualSteps,
  publishDocumentUpdates,
  undecidedCount,
  isPublishSheetOpen,
  publishStatus,
  onOpenPublishSheet,
  onClosePublishSheet,
  onDonePublish,
  onPublish,
  publishHistoryRecords,
  pendingManualStepsCount,
  isPublishHistoryOpen,
  isClearingPublishHistory,
  onOpenPublishHistory,
  onClosePublishHistory,
  onClearPublishHistory,
  canAcceptSelectedItem,
  acceptDisabledReason,
}: ReviewPanelProps) {
  const isSelectedItemPublished = selectedDecision === "synced";

  // Keyboard navigation: ArrowLeft/ArrowRight to switch between items
  useEffect(() => {
    if (Platform.OS !== "web") return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input/textarea
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement)?.isContentEditable) return;

      if (e.key === "ArrowRight") {
        e.preventDefault();
        onNavigateNext?.();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        onNavigatePrev?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onNavigateNext, onNavigatePrev]);

  return (
    <View style={styles.page}>
      <ReviewTopBar
        review={review}
        reviewedProgress={reviewedProgress}
        lastSavedAt={lastSavedAt}
        isSaving={isSaving}
        publishHistoryCount={publishHistoryRecords.length}
        pendingManualStepsCount={pendingManualStepsCount}
        onExitReview={onExitReview}
        onOpenPublishSheet={onOpenPublishSheet}
        onOpenPublishHistory={onOpenPublishHistory}
      />

      <View style={styles.workspace}>
        <ReviewSidebar
          review={review}
          selectedItemId={selectedItemId}
          filterMode={filterMode}
          collapsedSectionIds={collapsedSectionIds}
          getDecision={getDecision}
          onSelectItem={onSelectItem}
          onToggleSection={onToggleSection}
          onFilterModeChange={onFilterModeChange}
          onAcceptGroup={onAcceptGroup}
          onRejectGroup={onRejectGroup}
        />

        <View style={styles.contentColumn}>
          <ReviewActionPanel
            selectedReviewItem={selectedReviewItem}
            decision={selectedDecision}
            onAccept={onAccept}
            onReject={onReject}
            onUndo={onUndo}
            onNavigateNext={onNavigateNext}
            onNavigatePrev={onNavigatePrev}
            canAcceptSelectedItem={canAcceptSelectedItem}
            acceptDisabledReason={acceptDisabledReason}
          />

          <ScrollView
            style={styles.contentScroll}
            contentContainerStyle={styles.contentScrollContent}
            tabIndex={0}
            showsVerticalScrollIndicator>
            <View style={styles.contentInner}>
              <ReviewContentPanel
                review={review}
                selectedReviewItem={selectedReviewItem}
                activityDetails={activityDetails}
                isEditDisabled={isSelectedItemPublished}
                onStartReviewing={onStartReviewing}
                onSaveEdit={onSaveEdit}
                onCreateIle={onCreateIle}
              />
            </View>
          </ScrollView>
        </View>
      </View>

      <PublishSheet
        open={isPublishSheetOpen}
        items={queuedPublishItems}
        publishedItems={lastPublishedItems}
        manualSteps={publishManualSteps}
        documentUpdates={publishDocumentUpdates}
        undecidedCount={undecidedCount}
        lmsLabel={review.sourceLabel}
        status={publishStatus}
        onClose={onClosePublishSheet}
        onDone={onDonePublish}
        onPublish={onPublish}
      />
      <PublishHistorySheet
        open={isPublishHistoryOpen}
        records={publishHistoryRecords}
        lmsLabel={review.sourceLabel}
        isClearing={isClearingPublishHistory}
        onClose={onClosePublishHistory}
        onClearHistory={onClearPublishHistory}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    ...(Platform.OS === "web"
      ? ({
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          height: "100vh",
          overflow: "hidden",
          display: "flex",
        } as unknown as ViewStyle)
      : {}),
  },
  workspace: {
    flex: 1,
    flexDirection: "row",
    backgroundColor: colors.grey[50],
    minHeight: 0,
    ...(Platform.OS === "web"
      ? ({
          display: "flex",
          overflow: "hidden",
        } as const)
      : {}),
  },
  contentColumn: {
    flex: 1,
    minWidth: 0,
    backgroundColor: colors.grey[50],
  },
  contentScroll: {
    flex: 1,
    minHeight: 0,
    overflow: "scroll",
  },
  contentScrollContent: {
    maxWidth: 1024,
    alignSelf: "center",
    width: "100%",
    padding: 24,
    flexGrow: 1,
  },
  contentInner: {
    width: "100%",
    gap: 16,
  },
});

export default ReviewPanel;
